import * as THREE from 'three';
import { NOISE_GLSL } from './glsl.js';

const PARTICLE_COUNT = 80000;
const DOME_R = 290;

// ===== AMBIENT PARTICLE CONTROLS =====
// Vertical offset of the entire particle cloud (positive = up, negative = down)
const VERTICAL_OFFSET = -11;

// Noise strength – how much the curl noise affects particle positions
// Higher values = more chaotic, lower = calmer. (Default 85)
const NOISE_STRENGTH = 85.0;

// Noise frequency – scale of the input position for the curl noise
// Higher values = more detail (smaller swirls), lower = broader patterns. (Default 0.004)
const NOISE_FREQUENCY = 0.002;
// ======================================

const VERT = `
${NOISE_GLSL}
uniform float uTime;
uniform float uCurlSpeed;
uniform float uNoiseStrength;   // ← new
uniform float uNoiseFrequency;  // ← new
attribute float aSize;
varying  float vAlpha;
varying  vec3  vColor;

void main(){
  vec3  np    = position * uNoiseFrequency + vec3(0., uTime * uCurlSpeed * .055, uTime * uCurlSpeed * .038);
  vec3  c     = curlNoise(np);
  vec3  pos   = position + c * uNoiseStrength;

  // Cap particles inside the dome — clamp XZ to dome radius, cap Y
  float DOME_CAP = 289.;
  float d = length(pos);                              // 3D distance from center
  if (d > DOME_CAP) pos *= DOME_CAP / d;             // clamp to sphere surface
  pos.y = max(pos.y, -8.);                           // don't go below ground

  vec4 mv = modelViewMatrix * vec4(pos, 1.);
  gl_PointSize = aSize * (240. / max(1., -mv.z));
  gl_Position  = projectionMatrix * mv;

  float cn  = snoise(position * .008) * .5 + .5;
  vColor = mix(vec3(.91,.46,.16), vec3(1.,.72,.28), cn);
  
  float dist = length(position.xz) / 400.;
  vAlpha = (1. - clamp(dist, 0., 1.)) * .38 * smoothstep(-5., 28., pos.y);
}
`;
const FRAG = `
varying float vAlpha;
varying vec3  vColor;
void main(){
  vec2  c = gl_PointCoord - .5;
  float d = length(c);
  if(d > .5) discard;
  gl_FragColor = vec4(vColor, vAlpha * smoothstep(.5, .1, d));
}
`;

export class AmbientParticles {
  constructor(scene) {
    this.scene = scene;
    this._mat  = null;
  }

  init() {
    const pos   = new Float32Array(PARTICLE_COUNT * 3);
    const sizes = new Float32Array(PARTICLE_COUNT);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const r     = Math.random() * DOME_R * 1.4;
      const theta = Math.random() * Math.PI * 2;
      const x = Math.cos(theta) * r;
      const z = Math.sin(theta) * r;
      const y = Math.random() * DOME_R * 0.98;
      
      pos[i * 3]     = x;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = z;
      sizes[i] = Math.random() * 4.5 + 1.5;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos,   3));
    geo.setAttribute('aSize',    new THREE.BufferAttribute(sizes, 1));

    this._mat = new THREE.ShaderMaterial({
      vertexShader:   VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime:           { value: 0 },
        uCurlSpeed:      { value: 1.0 },
        uNoiseStrength:  { value: NOISE_STRENGTH },  // ← set from constant
        uNoiseFrequency: { value: NOISE_FREQUENCY }, // ← set from constant
      },
      transparent: true,
      depthWrite:  false,
      blending:    THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(geo, this._mat);
    particles.position.y = VERTICAL_OFFSET;
    this.scene.add(particles);
  }

  update(time, curlSpeed = 1.0) {
    if (!this._mat) return;
    this._mat.uniforms.uTime.value      = time;
    this._mat.uniforms.uCurlSpeed.value = curlSpeed;
    // If you want to change noise strength/frequency dynamically,
    // you can also update them here, e.g.:
    // this._mat.uniforms.uNoiseStrength.value = newValue;
  }
}